import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdArticle, MdClose, MdPrint, MdRefresh, MdArrowBack } from 'react-icons/md';
import api from '../../services/api';
import { useCard } from '../../contexts/cardContext';
import { buildReportViewModel, getMissingFields } from './reportData';
import { ContractReport, ProposalReport } from './templates';
import './style.css';

const REPORTS = [
  {
    id: 'proposal',
    title: 'Proposta Comercial - Sistema Construtivo',
    description: 'Proposta comercial com dados do card, cliente, pedido e produção.',
  },
  {
    id: 'contract',
    title: 'Contrato de Fornecimento e Cessão de Tecnologia',
    description: 'Contrato com dados do contratante, valores, pagamento e carga.',
  },
];

const getToday = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 10);
};

function DocumentosRelatorios({ idCard }) {
  const { setOpenCloseDocumentosRelatoriosModal } = useCard();
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documentDate, setDocumentDate] = useState(getToday());

  const closeModal = useCallback(
    () => setOpenCloseDocumentosRelatoriosModal(false),
    [setOpenCloseDocumentosRelatoriosModal],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (selectedReport) setSelectedReport(null);
        else closeModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeModal, selectedReport]);

  const loadReportData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/card/${idCard}/document-report-data`);
      const mapped = buildReportViewModel(response.data);
      setReportData(mapped);
      return mapped;
    } catch (requestError) {
      const message = requestError?.response?.data?.message
        || requestError?.response?.data?.error
        || 'Não foi possível carregar os dados associados ao card.';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const openReport = async (reportId) => {
    const data = await loadReportData();
    if (data) setSelectedReport(reportId);
  };

  const missingFields = useMemo(() => (
    selectedReport && reportData ? getMissingFields(selectedReport, reportData) : []
  ), [selectedReport, reportData]);

  const handlePrint = () => {
    const previousTitle = document.title;
    const reportLabel = selectedReport === 'contract' ? 'contrato' : 'proposta';
    document.title = `${reportLabel}_${reportData?.proposalCode || idCard}`;
    window.print();
    document.title = previousTitle;
  };

  return (
    <div className="documents-reports-modal" role="dialog" aria-modal="true" aria-label="Documentos e Relatórios">
      <div className={`documents-reports-container ${selectedReport ? 'report-open' : ''}`}>
        <header className="documents-reports-header no-print">
          <div>
            <h2>Documentos e Relatórios</h2>
            <p>Card #{idCard}</p>
          </div>
          <button type="button" onClick={closeModal} aria-label="Fechar"><MdClose /></button>
        </header>

        {!selectedReport && (
          <main className="documents-reports-list no-print">
            <p className="documents-reports-intro">Selecione um documento. Ao abrir, os dados atuais associados ao card serão carregados.</p>
            {error && <div className="documents-reports-error">{error}</div>}
            {REPORTS.map((report) => (
              <article className="documents-reports-item" key={report.id}>
                <div className="documents-reports-item-icon"><MdArticle /></div>
                <div className="documents-reports-item-content">
                  <h3>{report.title}</h3>
                  <p>{report.description}</p>
                </div>
                <button type="button" onClick={() => openReport(report.id)} disabled={loading}>
                  {loading ? 'Carregando...' : 'Ver relatório'}
                </button>
              </article>
            ))}
          </main>
        )}

        {selectedReport && reportData && (
          <>
            <div className="documents-reports-toolbar no-print">
              <button type="button" onClick={() => setSelectedReport(null)}><MdArrowBack /> Voltar</button>
              <label>
                Data do documento
                <input type="date" value={documentDate} onChange={(event) => setDocumentDate(event.target.value)} />
              </label>
              <button type="button" onClick={loadReportData} disabled={loading}><MdRefresh /> Atualizar dados</button>
              <button type="button" className="primary" onClick={handlePrint}><MdPrint /> Imprimir / Salvar PDF</button>
            </div>

            {error && <div className="documents-reports-error no-print">{error}</div>}
            {missingFields.length > 0 && (
              <div className="documents-reports-warning no-print">
                <strong>Dados ainda não informados:</strong> {missingFields.join(', ')}. O relatório pode ser visualizado, mas esses pontos aparecerão destacados.
              </div>
            )}

            <main className="documents-reports-preview">
              {selectedReport === 'proposal'
                ? <ProposalReport data={reportData} documentDate={documentDate} />
                : <ContractReport data={reportData} documentDate={documentDate} />}
            </main>
          </>
        )}
      </div>
    </div>
  );
}

export default DocumentosRelatorios;
